/**
 * Readers for a rejected async-thunk payload.
 *
 * Every thunk these helpers are used with (the quest mutation thunks in
 * store/tasks and the deliverable upload in store/dashboard) rejects with the
 * RAW response body, the shape Laravel's ResponseUtil::makeError produces:
 *
 *   { success: false, message: "...", data: { code: "typed_code" } }
 *
 * with a plain string fallback when the request never reached the server
 * (`rejectWithValue(error.response?.data || "Failed to ...")`). These two
 * helpers read that contract in one place so a component catch never has to
 * guess at the shape.
 *
 * WHY THIS EXISTS. Two failure classes in this codebase came from components
 * inventing an error shape instead of reading the real one: a catch that read
 * `error.errors` (a key the API never sends, so the failure rendered as
 * nothing at all), and a delete dialog that assumed success because it never
 * inspected the rejection. Both surfaced as silent, wrong success.
 *
 * NOT for thunks that omit `rejectWithValue`. Those reject with RTK's
 * serialized JS error, whose `message` is axios boilerplate ("Request failed
 * with status code 409") and must never reach a student. No caller of these
 * helpers reads such a thunk.
 */

/**
 * The typed `code` a backend refusal carries, or null when there is none.
 *
 * The codes are declared backend-side as constants (for example
 * RevealArtifactGuard::REASON_CODE) precisely so the frontend can branch on an
 * identifier instead of matching prose.
 */
export const apiErrorCode = (payload) => {
  const code = payload?.data?.code;
  return typeof code === "string" && code !== "" ? code : null;
};

/**
 * The server's own message, or `fallback` when the rejection carries none.
 *
 * Callers that must NOT show server prose (a typed refusal with its own
 * student-facing copy) branch on `apiErrorCode` first and never reach here.
 */
export const apiErrorMessage = (payload, fallback) => {
  if (typeof payload === "string" && payload.trim() !== "") {
    return payload;
  }
  const message = payload?.message;
  if (typeof message === "string" && message.trim() !== "") {
    return message;
  }
  return fallback;
};
